import { useCallback, useEffect, useMemo, useState } from 'react';

import { type FirstClassDocument, type PartialDocument, Category, DocumentSpine } from '../../types';
import {
  type DocumentChunk,
  type DocumentChunkContentMap,
  type DocumentChunkManifest,
  type DocumentChunkMap,
  type DocumentChunkTextContent,
  type WebviewDocumentChunk,
  DocumentChunkType,
UNCHUNKED_DOCUMENT_CHUNK_ID,
  WebviewChunks } from '../../types/chunkedDocuments';
import { isArticle, isChunkedDocument, isFirstClassDocument, notEmpty } from '../../typeValidators';
// eslint-disable-next-line import/no-cycle
import exceptionHandler from '../../utils/exceptionHandler.platform';
import getUrlDomain from '../../utils/getUrlDomain';
import makeLogger from '../../utils/makeLogger';
import { fetchRelatedRSS } from '../methods';
import { useIsStaffProfile } from '../models';
import background from '../portalGates/toBackground';
import { fetchDocumentContent } from '../stateUpdaters/transientStateUpdaters/documentContent';
import { resolveRelativePath } from '../utils/getChunkContentFromFilename';
import { extractImageUrls, inlineImages } from '../utils/inlineImages';
import { extractTopLevelNodeIndex } from '../utils/locationSerialization/utils';
import { unpackChunk } from '../utils/unpackChunk';
import useStatePlusLiveValueRef from '../utils/useStatePlusLiveValueRef';
import { usePartialDocument } from './index';

const logger = makeLogger(__filename, { shouldLog: false });

export const sortDocumentSpineByChunkIds = (spine: DocumentSpine): string[] => Object.entries(spine)
  .map(([index, id]) => [Number.parseInt(index, 10), id] as [number, string])
  .sort(([idx1, _id1], [idx2, _id2]) => idx1 - idx2)
  .map(([_, id]) => id);

/**
 * Loads set of chunks from disk and decrypts them if necessary, returning a map from chunk ID to chunk content.
 */
function useUnpackedDocumentChunks(
  chunkMap: DocumentChunkMap | undefined,
  chunkIds: string[],
  manifest: DocumentChunkManifest | undefined,
  isContentUnpackingDisabled: boolean | undefined,
): DocumentChunkContentMap {
  const [chunkContentMap, setChunkContentMap, chunkContentMapRef] =
    useStatePlusLiveValueRef<DocumentChunkContentMap>({});

  const chunksWhichShouldBeLoaded = useMemo(() => {
    if (!chunkMap) {
      return [];
    }
    return chunkIds
      .map((chunkId) => {
        const chunk = chunkMap[chunkId];
        if (!chunk) {
          logger.warn('Could not find chunk with ID', { chunkId });
          return undefined;
        }
        return chunk;
      })
      .filter(notEmpty);
  }, [chunkIds, chunkMap]);

  const inlineFileChunks = useCallback(
    async (html: string, filename: string) => {
      if (!manifest) {
        return html;
      }
      const imageAbsolutePaths = extractImageUrls(html).map((url) => resolveRelativePath(filename, url));
      if (imageAbsolutePaths.length === 0) {
        return html;
      }

      const imageChunksByFilename =
        await background.loadChunksByFilenameFromManifest(manifest, imageAbsolutePaths);

      return inlineImages(html, imageChunksByFilename, filename);
    },
    [manifest],
  );


  // NOTE: this useEffect can be expensive because it might decrypt many chunks, on the main thread.
  //   therefore, ensure it is not run too often lest we degrade app performance.
  useEffect(() => {
    if (isContentUnpackingDisabled) {
      return;
    }

    (async () => {
      const chunksToUnpack = chunksWhichShouldBeLoaded.filter(
        (chunk) => !(chunk.internal_id in chunkContentMapRef.current),
      );
      logger.debug('Unpacking chunks', {
        chunksToUnpack: chunksToUnpack.map((chunk) => [chunk.internal_id, chunk.id]),
        unpackedChunks: Object.values(chunkContentMapRef.current).map((chunk) => chunk?.id),
      });
      const unpackedChunks = await Promise.all(
        chunksToUnpack.map(async (chunk) => {
          const chunkContent = await unpackChunk(chunk);
          if (!chunkContent) {
            return undefined;
          }
          if (chunkContent.type === DocumentChunkType.Document) {
            chunkContent.text = await inlineFileChunks(chunkContent.text, chunk.filename);
          }
          logger.debug('unpacked chunk content', {
            id: chunk.internal_id,
            size: chunkContent.text?.length,
          });
          return chunkContent as DocumentChunkTextContent;
        }),
      );
      setChunkContentMap((existingEntries) => {
        const chunkIdsWhichShouldBeLoaded = new Set(chunksWhichShouldBeLoaded.map((chunk) => chunk.internal_id));
        const filteredExistingEntries = Object
          .entries(existingEntries)
          .filter(([chunkId, _]) => chunkIdsWhichShouldBeLoaded.has(chunkId));
        const newEntries = unpackedChunks
          .filter(notEmpty)
          .map((chunk) => [chunk.id, chunk]) as [string, DocumentChunkTextContent | undefined][];
        return Object.fromEntries(
          filteredExistingEntries.concat(newEntries),
        );
      });
    })();
    // we use chunkContentMapRef here instead of chunkContentMap itself to prevent an infinite render loop.
  }, [chunkContentMapRef, chunksWhichShouldBeLoaded, inlineFileChunks, setChunkContentMap, isContentUnpackingDisabled]);
  return chunkContentMap;
}

function useStartChunkIndex(
  doc: PartialDocument<FirstClassDocument, 'currentScrollPosition'> | null,
  chunkArray: DocumentChunk[] | null,
): number | undefined {
  const [startChunkIndex, setStartChunkIndex] = useState<number | undefined>();

  useEffect(() => {
    if (startChunkIndex !== undefined) {
      return;
    }
    if (!doc) {
      setStartChunkIndex(undefined);
      return;
    }
    const serializedPosition = doc.currentScrollPosition?.serializedPosition;
    if (!serializedPosition) {
      // doc is loaded but no current scroll position, so this is the first open of this doc.
      setStartChunkIndex(0);
      return;
    }
    if (!chunkArray) {
      return;
    }
    let topLevelNodeIndex = extractTopLevelNodeIndex(serializedPosition);
    if (topLevelNodeIndex === undefined) {
      return;
    }
    let chunkIndex = 0;
    for (const chunk of chunkArray) {
      if (chunk.html_child_node_count === null) {
        exceptionHandler.captureException('chunk is missing top level node count', {
          extra: {
            chunk,
            chunkIndex,
            topLevelNodeIndex,
            serializedPosition,
          },
        });
        break;
      }
      if (topLevelNodeIndex < chunk.html_child_node_count) {
        break;
      }
      topLevelNodeIndex -= chunk.html_child_node_count;
      chunkIndex += 1;
    }
    logger.debug('calculated start chunk index', {
      serializedPosition,
      topLevelNodeIndex,
      chunkIndex,
    });
    setStartChunkIndex(chunkIndex);
  }, [chunkArray, doc, startChunkIndex]);

  return startChunkIndex;
}

/**
 * Loads all text chunks from disk/network. Note that the returned chunk map does NOT include binary chunks e.g.
 * images or audio.
 */
export function useTextChunkMap(
  manifest: DocumentChunkManifest | undefined,
  shouldForceUnchunkedContent?: boolean,
): DocumentChunkMap | undefined {
  const [chunkMap, setChunkMap] = useState<DocumentChunkMap | undefined>();
  useEffect(() => {
    if (shouldForceUnchunkedContent || !manifest) {
      setChunkMap(undefined);
      return;
    }
    let wasEffectCleanupRun = false;
    (async () => {
      logger.debug('Loading chunk map', { manifest: Object.keys(manifest) });
      const chunkMap = await background.loadAllTextChunksFromManifest(manifest);
      if (wasEffectCleanupRun) {
        return;
      }
      setChunkMap(chunkMap);
    })();
    return () => {
      wasEffectCleanupRun = true;
    };
  }, [manifest, shouldForceUnchunkedContent]);
  return chunkMap;
}

export type ChunkedDocumentContentOptions = {
  isChunkingDisabled?: boolean;
  isContentUnpackingDisabled?: boolean;
};

/**
 * Loads and decrypts 'window' of chunks around the last reading position of the given doc.
 * Chunks are guaranteed to be in order and have images inlined as base64 data URLs.
 *
 * For compatibility, if document is not chunked, returns a one-chunk array with full document content.
 *
 * @param docId
 * @param options
 *
 * @return Object array of chunk contents, plus a function to load or unload a chunk with a given ID.
 */
export function useChunksForDocument(
  docId: string | undefined,
  { isChunkingDisabled, isContentUnpackingDisabled }: ChunkedDocumentContentOptions = {},
) {
  const [doc, { isFetching }] = usePartialDocument(
    docId,
    [
      'category',
      'content',
      'currentScrollPosition',
      'html',
      'id',
      'parsed_doc_id',
      'source_specific_data',
      'transientData',
      'url',
      'word_count',
    ],
    {
      shouldPollStateIfMissing: true,
    },
  );

  // TODO: this code is duplicated from useDocumentContentFromState().
  //   eventually that hook code should be removed and any uses of it should use useChunkedDocumentContent() instead.
  const unchunkedContent = useMemo(() => {
    if (!doc) {
      return null;
    }
    return doc.category === Category.Highlight ? doc.html : doc.transientData.content;
  }, [doc]);

  const hasDoc = Boolean(doc);
  const parsedDocId = doc && isFirstClassDocument(doc) && doc?.parsed_doc_id;
  const articleDomain = doc && isArticle(doc) && getUrlDomain(doc.url);
  const isChunkedDoc = isChunkedDocument(doc);
  const isStaffProfile = useIsStaffProfile();

  const toc = useMemo(() => doc?.source_specific_data?.epub?.toc, [doc?.source_specific_data?.epub?.toc]);

  const shouldForceUnchunkedContent =
    (isChunkingDisabled ?? false) ||
    !isChunkedDoc ||
    !isStaffProfile; // TODO: remove when we want to enable chunking for non-staff users

  useEffect(() => {
    if (!docId || !hasDoc || isContentUnpackingDisabled) {
      return;
    }

    /*
      This is async. It isn't guaranteed the content will be there by the time this function returns.
      This is here to ensure the content is requested whenever we're trying to use it. It's called
      elsewhere too (to pre/load the content) but let's be safe. Even if the other calls are
      accidentally broken / removed, this will save us.
    */
    fetchDocumentContent([docId]);
    if (articleDomain) {
      fetchRelatedRSS([articleDomain]);
    }
  }, [docId, hasDoc, parsedDocId, articleDomain, isContentUnpackingDisabled]);

  const spine = useMemo(
    () =>
      shouldForceUnchunkedContent || !doc?.transientData.spine
        ? undefined
        : sortDocumentSpineByChunkIds(doc.transientData.spine),
    [doc?.transientData.spine, shouldForceUnchunkedContent],
  );
  const manifest = doc?.transientData.manifest;
  const chunkMap = useTextChunkMap(manifest, shouldForceUnchunkedContent);
  const chunkArray = useMemo(() => {
    if (!spine || !chunkMap) {
      return null;
    }
    const chunks = spine.map((id) => chunkMap[id]);
    if (!chunks.every(notEmpty)) {
      return null;
    }
    return chunks;
  }, [chunkMap, spine]);

  const startChunkIndex = useStartChunkIndex(doc, chunkArray);
  const chunkIndexesToLoad = useMemo(() => {
    if (startChunkIndex === undefined || shouldForceUnchunkedContent) {
      return undefined;
    }
    return Array.from({ length: 3 }, (_, index) => startChunkIndex - 1 + index).filter(
      (index) => index >= 0,
    );
  }, [startChunkIndex, shouldForceUnchunkedContent]);

  const [loadedDocumentChunkIds, setLoadedDocumentChunkIds] = useState<{
    [id: string]: boolean;
  }>({});

  useEffect(() => {
    logger.debug('clearing loaded chunks');
    setLoadedDocumentChunkIds({});
  }, [docId]);

  const styleChunks = useMemo(() => {
    if (!chunkMap || shouldForceUnchunkedContent) {
      return [];
    }
    return Object.values(chunkMap)
      .filter((chunk) => chunk.type === DocumentChunkType.Style);
  }, [chunkMap, shouldForceUnchunkedContent]);

  const allChunkIdsToLoad = useMemo(() => {
    const styleChunkIds = styleChunks.map((chunk) => chunk.internal_id);
    return [...Object.keys(loadedDocumentChunkIds), ...styleChunkIds];
  }, [loadedDocumentChunkIds, styleChunks]);

  const chunkContentMap = useUnpackedDocumentChunks(
    chunkMap,
    allChunkIdsToLoad,
    manifest,
    isContentUnpackingDisabled,
  );

  const setChunkContentState = useCallback((chunkId: string, state: 'loaded' | 'unloaded') => {
    if (chunkId === 'single-chunk-full-content') {
      return;
    }
    setLoadedDocumentChunkIds((ids) => {
      switch (state) {
        case 'loaded':
          if (chunkId in ids) {
            return ids;
          }
          logger.debug(`chunk content from store LOAD`, { id: chunkId });
          return {
            ...ids,
            [chunkId]: true,
          };
        case 'unloaded': {
          if (!(chunkId in ids)) {
            return ids;
          }
          logger.debug(`chunk content from store UNLOAD`, { id: chunkId });
          const newIds = { ...ids };
          delete newIds[chunkId];
          return newIds;
        }
      }
    });
  }, []);

  useEffect(() => {
    if (chunkIndexesToLoad === undefined) {
      return;
    }
    const idsToLoad = Object.fromEntries(
      chunkIndexesToLoad
        .map((index) => spine?.[index])
        .filter(notEmpty)
        .map((id) => [id, true]),
    );
    setLoadedDocumentChunkIds((ids) => ({
      ...ids,
      ...idsToLoad,
    }));
  }, [chunkIndexesToLoad, spine]);

  const webviewDocumentChunks: WebviewDocumentChunk[] | undefined = useMemo(
    () =>
      chunkArray
        ?.map((chunk, index) => {
          const { id: databaseId, internal_id: id } = chunk;
          if (chunk.html_child_node_count === null) {
            exceptionHandler.captureException(
              'generating WebviewDocumentChunk: text chunk has no html_child_node_count',
              {
                extra: {
                  id,
                  databaseId,
                  index,
                  chunkType: chunk.type,
                  dataLength: chunk.data.length,
                },
              },
            );
            return undefined;
          }
          return {
            id,
            index,
            filename: chunk.filename,
            content: chunkContentMap?.[id]?.text ?? null,
            html_child_node_count: chunk.html_child_node_count,
            word_count: chunk.word_count,
          };
        })
        ?.filter(notEmpty),
    [chunkContentMap, chunkArray],
  );

  const normalizedWebviewDocumentChunks: WebviewDocumentChunk[] = useMemo(() => {
    if (!shouldForceUnchunkedContent) {
      return webviewDocumentChunks ?? [];
    }
    if (unchunkedContent) {
      return [
        {
          content: unchunkedContent,
          index: 0,
          filename: UNCHUNKED_DOCUMENT_CHUNK_ID,
          // only used for chunked docs, so doesn't need to be accurate.
          html_child_node_count: 0,
          // NOTE: this ID is used to calculate html_child_node_count inside the content frame, do not change.
          id: UNCHUNKED_DOCUMENT_CHUNK_ID,
          // We default the word count of this one chunk for unchunked documents to the word count of the document.
          // As this is the only absolute word count used for this document, the relative progress will still be accurate..
          word_count: doc?.word_count ?? 0,
        },
      ];
    }
    return [];
  }, [doc?.word_count, shouldForceUnchunkedContent, unchunkedContent, webviewDocumentChunks]);

  const webviewStyleChunks = useMemo(() => styleChunks
    .map((chunk) => {
      const content = chunkContentMap?.[chunk.internal_id]?.text ?? null;

      return {
        id: chunk.internal_id,
        filename: chunk.filename,
        content,
      };
    })
    .filter(notEmpty),
  [styleChunks, chunkContentMap]);

  const chunks: WebviewChunks = useMemo(() => ({
    document: normalizedWebviewDocumentChunks,
    style: webviewStyleChunks,
    toc,
  }), [normalizedWebviewDocumentChunks, webviewStyleChunks, toc]);

  useEffect(() => {
    const loaded = webviewDocumentChunks?.filter((c) => c.content);
    logger.debug(`chunks ${loaded?.length}/${webviewDocumentChunks?.length} loaded`, {
      loaded: loaded?.map((c) => [c.index, c.id]),
    });
  }, [webviewDocumentChunks]);

  return useMemo(
    () => ({
      setChunkContentState,
      chunks,
      isFetching,
    }),
    [chunks, isFetching, setChunkContentState],
  );
}
